import { Component, effect, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '../../../components/data-table-builder/data-table-builder.component';
import { DialogComponent } from '../../../components/dialog/dialog.component';
import { DialogObject } from '@/components/dialog/dialog.types';
import { DataTableAction, DataTableRowActions, DataTableServerQuery, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { Ppm, TasksStatus } from '@/types';
import { PpmService } from '../ppm.service';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { getPpmDataTableStructure } from './utils/ppm.data-table-object';
import { PpmRepository } from '@/store/ppm-state/ppm-state.repository';
import { getPpmDeleteObject } from './utils/ppm-delete-dialog-object';
import { AuthorizationService } from '@/services/authorization.service';
import { createServerQuery } from '@/components/data-table-builder/server-query';

@Component({
  selector: 'sm-ppm-current-plan',
  templateUrl: './current-plans.component.html',
  styleUrl: './current-plans.component.scss',
  imports: [PageHeaderComponent, DataTableBuilderComponent, DialogComponent],
})
export class PpmCurrentPlansComponent implements OnInit {
  //services
  ppmService = inject(PpmService);
  ppmRepository = inject(PpmRepository);
  messageService = inject(MessageService);
  router = inject(Router);
  translate = inject(TranslateService);
  authService = inject(AuthorizationService);

  breadcrumb = [
    {
      label: 'ppm.plural',
      routerLink: ['ppm-current-plans'],
    },
  ];

  @ViewChild('dialog') dialogComponent!: DialogComponent;
  ppmDialog?: DialogObject;

  rowActionsFn: (target: Ppm) => DataTableRowActions = (target: Ppm) => {
    const additionalActions: Record<string, DataTableAction[] | undefined> = {};
    if (target.processInstanceId && target.status != TasksStatus.PUBLIEE)
      additionalActions['workflow'] = [
        {
          icon: 'pi pi-check',
          label: 'ppm.actions.treat',
          action: () => {
            this.router.navigate([`ppm-edit-plan`, target.id]);
          },
        },
      ];
    additionalActions['history'] = [
      {
        icon: 'pi pi-search',
        label: 'ppm.actions.access_history',
        action: () => {
          this.router.navigate([`ppm-version-history`, target.id]);
        },
      },
    ];
    if (target.processInstanceId)
      additionalActions['history'].push({
        icon: 'pi pi-search',
        label: 'ppm.actions.access_task_history',
        action: () => {
          this.router.navigate([`ppm-task-history`, target.processInstanceId]);
        },
      });
    return {
      defaultActions: {
        inspect: {
          label: 'ppm.actions.inspect',
        },
        edit: {
          label: 'ppm.actions.modify',
        },
        delete: {
          label: 'ppm.actions.delete',
        },
      },
      inspectAction: (row: Ppm) => {
        this.router.navigate([`/ppm-inspect-plan`, row.id]);
      },
      editAction:
        target.status === TasksStatus.BROUILLON && this.authService.isRedacteur()
          ? (row: Ppm) => {
              this.router.navigate([`/ppm-edit-plan`, row.id]);
            }
          : undefined,
      deleteAction:
        target.status === TasksStatus.BROUILLON && this.authService.isRedacteur()
          ? (row: Ppm) => {
              this.ppmDialog = getPpmDeleteObject({
                store: this.ppmRepository,
                representation: row.ref,
                action: () => {
                  this.deletePpm(row.id);
                  this.dialogComponent.toggleVisible();
                },
                toggleVisiblity: () => this.dialogComponent.toggleVisible(),
              });
              this.dialogComponent.toggleVisible();
            }
          : undefined,
      additionalActions,
    };
  };

  data: Ppm[] = [];
  totalRecords = 0;
  serverQuery: DataTableServerQuery = createServerQuery({ initialPageSize: 10, initialSortBy: 'updatedAt', initialSortOrder: 'desc' });
  dataTableObject?: DynamicDataTable;
  loading = true;

  constructor() {
    let firstRun = true;

    effect(() => {
      const page = this.serverQuery.page();
      const size = this.serverQuery.pageSize();
      const sortBy = this.serverQuery.sortBy();
      const sortOrder = this.serverQuery.sortOrder();
      const search = this.serverQuery.search();
      const sort = sortBy ? `${sortBy},${sortOrder}` : '';

      if (firstRun) {
        firstRun = false;
        return;
      }

      this.loadData(page, size, search, sort);
    });
  }

  ngOnInit(): void {
    this.dataTableObject = getPpmDataTableStructure({
      store: this.ppmRepository,
      createAction: this.authService.isRedacteur()
        ? () => {
            this.router.navigate(['/ppm-new-plan']);
          }
        : undefined,
      rowActionsFn: (target: Ppm) => this.rowActionsFn(target),
      serverQuery: this.serverQuery,
    });
    this.loadData();
  }

  loadData(page = 0, size = 10, search = '', sort = '') {
    this.ppmService.getCurrentUserPpm({ page, size, search, sort }).subscribe({
      next: data => {
        this.data = data.content;
        this.totalRecords = data.totalElements;
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('ppm.message.error.loading'),
        });
      },
    });
  }

  deletePpm(id: number) {
    return this.ppmService.deletePpm(id).subscribe({
      next: () => {
        this.loading = true;
        this.loadData();
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('ppm.message.success.deleted', {
            reference: this.ppmRepository.getNested('response', 'ref'),
          }),
        });
      },
      error: err => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant('ppm.message.error.deleted'),
        });
      },
    });
  }
}
